import SideBarElement, {
  SideBarElementItemProps,
} from '@/components/shared/sidebar/SidebarElement';
import {
  GiftIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { isEmpty, isRegulatedLocale } from '@/shared/utility';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { openSearchPallete } from '@/utils/utils';
import { cdnImage } from '@/utils/image-utils';
import { openDrawer } from '@/store/ui/actions';
import { DrawerName } from '@/store/ui/reducers';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAvailableBonuses } from '@/store/bonuses/selectors';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useIntl } from 'react-intl';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import { selectPromotionsHasAny } from '@/store/cms/promotions/selectors';
import { selectLocale } from '@/store/selectors/commonSelectors';
import SidebarMenu from '@/components/shared/sidebar/SidebarMenu';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import NotificationBadge from '@/components/UI/NotificationBadge';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

const SidebarNavigation = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const hasPromotions = useSelector(selectPromotionsHasAny);
  const locale = useSelector(selectLocale);
  const availableBonuses = useSelector(selectAvailableBonuses);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { openModal } = useModal();
  const { isEnabled: isRewardReelEnabled, pagePath: rewardsWheelPagePath } = useRewardsWheel();
  const isRestricted = useRestrictedMode();

  const sideBarElementsProps: SideBarElementItemProps[] = [
    {
      'aria-label': 'Logo Home navigation button',
      'aria-roledescription': 'Logo button used to navigate to home page',
      'data-cy': 'sidebar-home',
      id: 'home',
      to: '/',
      shouldShow: true,
      icon: <HomeIcon width={30} height={30} />,
      children: intl.formatMessage({ id: 'header.home' }),
    },
    {
      to: '/games',
      'data-cy': 'sidebar-games',
      id: 'games',
      shouldShow: true,
      onClick: () => {
        triggerGTag(GTagEvents.open_category, {
          openedFrom: OpenedFrom.Header,
          categoryName: 'game-lobby',
        });
      },
      icon: <RocketLaunchIcon width={30} height={30} />,
      children: intl.formatMessage({ id: 'header.games' }),
      'aria-label': 'Games navigation',
      'aria-roledescription': 'Navigates to games section',
    },
    {
      'data-cy': 'sidebar-search',
      id: 'search',
      shouldShow: true,
      onClick: () => {
        triggerGTag(GTagEvents.header_search_click);
        openSearchPallete();
      },
      icon: <MagnifyingGlassIcon width={30} height={30} />,
      children: intl.formatMessage({ id: 'games.search' }),
      'aria-label': 'Search Button',
      'aria-roledescription': 'Searches games and providers for the provided input',
    },
    {
      'data-cy': 'sidebar-bonuses',
      id: 'bonuses',
      shouldShow:
        isAuthenticated &&
        availableBonuses.length > 0 &&
        !isRegulatedLocale(locale) &&
        !isRestricted,
      onClick: () => {
        openModal(MODALS.AVAILABLE_BONUSES, { bonuses: availableBonuses });
      },
      icon: (
        <div className="relative">
          {!isEmpty(availableBonuses) && isAuthenticated && (
            <NotificationBadge count={availableBonuses.length} />
          )}
          <GiftIcon width={30} height={30} />
        </div>
      ),
      'aria-label': 'Bonuses Modal button',
      'aria-roledescription': 'Opens modal which contains available bonuses for user',
      children: intl.formatMessage({ id: 'misc.bonuses' }),
    },
    {
      id: 'rewards-wheel',
      'data-cy': 'sidebar-rewards-wheel',
      to: `/${rewardsWheelPagePath}`,
      onClick: () => {
        triggerGTag(GTagEvents.open_reward_reel, {
          openedFrom: 'menu-drawer',
        });
      },
      icon: (
        <img
          height="30"
          width="30"
          className="h-[30px] w-[30px]"
          src={cdnImage('rewards-wheel.png', {
            width: '30',
            height: '30',
            dpr: '2',
          })}
          alt={intl.formatMessage({ id: 'rewardReel.title' })}
        />
      ),
      shouldShow: isRewardReelEnabled,
      children: intl.formatMessage({
        id: 'rewardReel.title.cta',
      }),
      'aria-label': 'Rewards Wheel button',
      'aria-roledescription': 'Navigates to Rewards Wheel',
    },
    {
      to: '/promotions',
      id: 'promotions',
      icon: <StarIcon width={30} height={30} />,
      children: intl.formatMessage({ id: 'promotions.header' }),
      // Promotions are unavailable in restricted mode — hide the entry point (the page
      // still renders PromotionsRestrictedNotice as a safety net for direct URLs).
      shouldShow: hasPromotions && !isRestricted,
      'aria-label': 'Promotions button',
      'aria-roledescription': 'Navigates to page where all available promotions are displayed',
    },
    {
      'data-cy': 'sidebar-help',
      id: 'help',
      icon: <QuestionMarkCircleIcon width={30} height={30} />,
      children: intl.formatMessage({ id: 'header.help' }),
      shouldShow: locale !== 'eu',
      onClick: () => {
        triggerGTag(GTagEvents.header_help_click);
        dispatch(openDrawer(DrawerName.Help));
      },
      'aria-label': 'Help Drawer open button',
      'aria-roledescription': 'Opens Help Drawer',
    },
  ];

  return (
    <SidebarMenu>
      {sideBarElementsProps.map(({ children, id, ...elementProps }) => (
        <SideBarElement key={`sidebar-navigation-element-${id}`} {...elementProps}>
          {children}
        </SideBarElement>
      ))}
    </SidebarMenu>
  );
};

export default SidebarNavigation;
